import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { finalize, take } from 'rxjs/operators';

import { ApiError } from '../../../../core';
import { CampanhaSearchOrderBy } from '../../../../home/campanhas/campanhas/campanha-search-orderby';
import {
  AreaCampanhaSelect,
  createAreasCampanhasSelect
} from '../../../../home/campanhas/campanhas/components/select';
import { CampanhaFilter } from '../../../../home/campanhas/campanhas/filter/campanha-filter';
import { CampanhaSearchHttpService } from '../../../../home/campanhas/campanhas/services';
import { campanhaStatusEmAndamento } from '../../../../home/campanhas/campanhas/status/campanha-status';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { MenuComponent } from '../menu/menu.component';
import {
  IGroup,
  MultiSelectGroupedComponent
} from '../multi-select-grouped/multi-select-grouped.component';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { ToastService } from '../toast/services/toast.service';

type ObjetivoCampanha = 'venda' | 'bonificacao' | 'aquisicao';
type StatusCampanha = 'emAndamento';

@Component({
  selector: 'sgt-novo-campanha-multi-select',
  templateUrl: './novo-campanha-multiselect.component.html',
  styleUrls: ['./novo-campanha-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoCampanhaMultiselectComponent),
      multi: true
    }
  ]
})
export class NovoCampanhaMultiselectComponent
  extends CustomControlValueAccessor<number[]>
  implements OnInit {
  @Input() idsCampanhas: number[] = [];
  @Input() variante: 'filtro' | undefined;
  @Input() settings: Partial<IMultiSelectSettings> = {};

  @Input() statusCampanha: Array<StatusCampanha> = [];
  @Input() objetivoCampanha: Array<ObjetivoCampanha> = [];

  @Output() idsCampanhasChange = new EventEmitter<number[]>();

  @ViewChild('menuRef', { static: false }) menuRef: MenuComponent | undefined;
  @ViewChild('multiSelectRef', { static: false }) multiSelectRef:
    | MultiSelectGroupedComponent
    | undefined;

  somenteEmAndamento = true;

  groups: IGroup[] = [];
  defaultSettings: IMultiSelectSettings = {
    placeholder: 'Campanhas',
    singular: 'Campanhas',
    plural: 'Campanhas',
    searchEmptyResultMessage: 'Nenhuma campanha encontrada',
    showFooter: false
  };

  constructor(
    private toastService: ToastService,
    private campanhaSearchHttpService: CampanhaSearchHttpService
  ) {
    super();
  }

  ngOnInit(): void {
    this.settings = {
      ...this.defaultSettings,
      ...this.settings
    };
  }

  firstOpened(isFirstOpened: boolean): void {
    if (isFirstOpened && this.groups.length === 0) {
      this.search();
    }
  }

  onHasOpened(isOpen: boolean): void {
    if (isOpen) {
      this.menuRef?.closeMenu();
    }
  }

  onMenuOpened(): void {
    this.multiSelectRef?.closeDropdown();
  }

  onSomenteEmAndamentoChange(): void {
    this.search();
  }

  private createFilter(): CampanhaFilter {
    const filter = new CampanhaFilter(null);

    if (this.somenteEmAndamento) {
      filter.statuses = [
        ...(filter.statuses ?? []),
        campanhaStatusEmAndamento.id
      ];
    }

    const statusMap: Record<StatusCampanha, () => void> = {
      emAndamento: () =>
        (filter.statuses = [
          ...(filter.statuses ?? []),
          campanhaStatusEmAndamento.id
        ])
    };
    this.statusCampanha.forEach((status) => statusMap[status]?.());

    const objetivoMap: Record<ObjetivoCampanha, () => void> = {
      venda: () => (filter.objetivoVenda = true),
      bonificacao: () => (filter.objetivoBonificacao = true),
      aquisicao: () => (filter.objetivoAquisicao = true)
    };
    this.objetivoCampanha.forEach((objetivo) => objetivoMap[objetivo]?.());

    return filter;
  }

  private search(): void {
    this.groups = [];

    const filter = this.createFilter();
    this.definirCarregando(true);

    this.campanhaSearchHttpService
      .searchSelect(filter, CampanhaSearchOrderBy.NomeArea, 'asc')
      .pipe(take(1), finalize(() => this.definirCarregando(false)))
      .subscribe(
        (campanhas) => {
          const areasCampanhas: AreaCampanhaSelect[] =
            createAreasCampanhasSelect(campanhas);
          const currentIds = this.value ?? [];
          if (areasCampanhas) {
            this.groups = areasCampanhas.map((area) => ({
              id: area.id,
              name: area.nome,
              options: (area.campanhas ?? []).map((campanha) => ({
                id: campanha.id,
                name: campanha.nome,
                isChecked: currentIds.includes(campanha.id)
              }))
            }));
          }
        },
        (error: ApiError) => {
          this.toastService.error(error.message, { duration: 5000 });
        }
      );
  }

  onSelectionChange(): void {
    this.emitChange(this.idsCampanhas);
  }

  onSearch(): void {
    this.idsCampanhasChange.emit(this.idsCampanhas);
  }

  protected updateValue(newValue: number[] | null): void {
    this.idsCampanhas = newValue ?? [];
    if (this.idsCampanhas.length > 0 && this.groups.length === 0) {
      this.search();
    }
  }

  private definirCarregando(carregando: boolean): void {
    this.settings = { ...this.settings, isLoading: carregando };
  }

}
